import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Worker, Viewer, SpecialZoomLevel, LoadError } from '@react-pdf-viewer/core';
import { defaultLayoutPlugin } from '@react-pdf-viewer/default-layout';
import '@react-pdf-viewer/core/lib/styles/index.css';
import '@react-pdf-viewer/default-layout/lib/styles/index.css';

interface SafePdfViewerProps {
  pdfUrl: string;
  filename?: string;
  /** Reading zoom factor from the viewer chrome, 1 = 100%. */
  zoom?: number;
}

/**
 * WHY THE SCALE IS PINNED (files-12).
 *
 * With `defaultScale` left as a SpecialZoomLevel, react-pdf-viewer keeps that
 * level alive: every container resize re-fits the page to whatever width the
 * frame happens to have that moment (`keepSpecialZoomLevelRef`). Collapsing
 * the sidebar or toggling expanded view therefore re-laid-out the sheet
 * repeatedly and it appeared to swing left and right — and any zoom the reader
 * had chosen was silently overwritten.
 *
 * So: fit to width ONCE, then convert that fit into a fixed number by calling
 * `zoomTo` with it, which clears the library's "keep this level" flag. From
 * then on the page keeps its size and a wider window adds margin instead of
 * re-scaling. The zoom control multiplies that pinned base.
 */
const SafePdfViewer: React.FC<SafePdfViewerProps> = ({ pdfUrl, filename = 'document.pdf', zoom = 1 }) => {
  const { t } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const defaultLayoutPluginInstance = React.useMemo(() => defaultLayoutPlugin(), []);
  const zoomTo = defaultLayoutPluginInstance.toolbarPluginInstance.zoomPluginInstance.zoomTo;
  const containerRef = useRef<HTMLDivElement | null>(null);

  // The fit-to-width scale, resolved on load and never recomputed by a resize.
  const baseScaleRef = useRef<number | null>(null);
  const appliedZoomRef = useRef<number>(zoom);

  /*
    SAME ORIGIN, ALWAYS. This fetched the worker from a public npm CDN, which
    is two problems in a deployed environment: a Content-Security-Policy whose
    `script-src`/`worker-src` does not name that host blocks it outright, and a
    firm running PONS in the EU has just been made to depend on a third party
    to open its own document.

    `public/pdf.worker.min.js` is served by us, from our origin, and is kept in
    step with the installed `pdfjs-dist` by `scripts/sync-pdf-worker.js` —
    which exists because the committed copy had drifted to 4.8.69 against a
    3.11.174 API and pdf.js refused every document with a version mismatch.
  */
  const workerUrl = `${process.env.PUBLIC_URL || ''}/pdf.worker.min.js`;

  // Mirrors the library's own PageWidth arithmetic (core: calculateScale).
  const SCROLL_BAR_WIDTH = 17;

  const handleDocumentLoad = useCallback(
    async (e: { doc: any }) => {
      setError(null);
      try {
        const page = await e.doc.getPage(1);
        const pageWidth = page.getViewport({ scale: 1 }).width;
        if (!pageWidth) return;
        requestAnimationFrame(() => {
          const pages = containerRef.current?.querySelector('.rpv-core__inner-pages') as HTMLElement | null;
          const available = pages?.clientWidth || containerRef.current?.clientWidth || 0;
          if (!available) return;
          const base = (available - SCROLL_BAR_WIDTH) / pageWidth;
          baseScaleRef.current = base;
          appliedZoomRef.current = zoom;
          // A NUMERIC scale — this is what clears the library's
          // "keep re-fitting to the container" flag.
          zoomTo(base * zoom);
        });
      } catch (loadError) {
        console.error('[SafePdfViewer] Could not resolve page size:', loadError);
      }
    },
    [zoom, zoomTo]
  );

  useEffect(() => {
    if (baseScaleRef.current === null || appliedZoomRef.current === zoom) return;
    appliedZoomRef.current = zoom;
    zoomTo(baseScaleRef.current * zoom);
  }, [zoom, zoomTo]);

  // A different document starts over.
  useEffect(() => {
    baseScaleRef.current = null;
  }, [pdfUrl]);

  const renderErrorFallback = (error: LoadError) => {
    console.error('[SafePdfViewer] PDF Load Error:', error);
    return (
      <div className="flex items-center justify-center h-full bg-danger-50 dark:bg-danger-900/20">
        <div className="glass p-6 rounded-lg -danger-200 dark:-danger-800 max-w-sm text-center">
          <p className="text-danger-700 dark:text-danger-300 font-medium mb-2">{t('Failed to Render PDF')}</p>
          <p className="text-sm text-danger-600 dark:text-danger-400">{error.message}</p>
          <p className="text-xs text-danger-500 dark:text-danger-500 mt-2">{t('Check browser console for details')}</p>
        </div>
      </div>
    );
  };

  if (error) {
    return (
      <div className="flex items-center justify-center h-full bg-danger-50 dark:bg-danger-900/20">
        <div className="glass p-6 rounded-lg -danger-200 dark:-danger-800 max-w-sm">
          <p className="text-danger-700 dark:text-danger-300 font-medium mb-2">{t('Unable to Display PDF')}</p>
          <p className="text-sm text-danger-600 dark:text-danger-400 mb-3">{error}</p>
          <p className="text-xs text-danger-500 dark:text-danger-400 font-mono break-all">{filename}</p>
        </div>
      </div>
    );
  }

  /*
    ONE scroller, and it is this one (files-4). The viewer owns the full width
    of the region it is given, so its bar sits at the page's right edge;
    `scrollbar-gutter: stable` reserves that gutter whether or not the bar is
    showing, which is the other half of the swinging fix — the page can no
    longer oscillate between "bar" and "no bar" widths.
  */
  return (
    <div
      ref={containerRef}
      className="w-full h-full bg-surface-sunken overflow-hidden flex flex-col"
      style={{ scrollbarGutter: 'stable' }}
    >
      <div className="flex-1 min-h-0 w-full">
        <Worker workerUrl={workerUrl}>
          <Viewer
            fileUrl={pdfUrl}
            plugins={[defaultLayoutPluginInstance]}
            defaultScale={SpecialZoomLevel.PageWidth}
            onDocumentLoad={handleDocumentLoad}
            renderError={renderErrorFallback}
          />
        </Worker>
      </div>
    </div>
  );
};

export default SafePdfViewer;
